import React, { useEffect, useState } from "react";

import SettingsSidebar from "../../partials/settings/SettingsSidebar";
import PayoutsPanel from "../../partials/settings/PayoutsPanel.jsx";
import BillingPanel from "../../partials/settings/BillingPanel.jsx";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import Customize from "../../partials/settings/customize/index.jsx";
import { useDispatch, useSelector } from "react-redux";
import PremiumPaywall from "../../components/PremiumPaywall.jsx";
import { PrimaryButton } from "../../components/Buttons.jsx";
import {
  getSellerCustomDomainJob,
  submitCustomDomain,
  getStore,
} from "../../api/settings.js";
import { addToast } from "../../store/app/action.js";
import { FaCaretDown, FaCaretUp } from "react-icons/fa6";

function Customization() {
  const CustomDomainStatusDot = () => {
    let color;
    let copy;

    if (currentJob) {
      switch (currentJob.status) {
        case "inactive":
          color = "bg-slate-400";
          copy = "No custom domain connected";
          break;
        case "pending":
          color = "bg-yellow-400";
          copy = "Custom domain pending activation, check back later";
          break;
        case "active":
        case "completed":
          color = "bg-green-400";
          copy = "Custom domain connected";
          break;
        case "error":
          color = "bg-red-400";
          copy =
            "Error connecting custom domain, enter a new domain to try again";
          break;
        default:
          color = "bg-slate-400";
          copy = "No custom domain connected";
      }
      return (
        <>
          <span
            className={`h-3 w-3 rounded-full ${color} inline-block`}
            title={status}
          ></span>
          <span className="ml-2">{copy}</span>
        </>
      );
    } else if (fullLocationDetail?.custom_direct_web_urls?.length) {
      return (
        <>
          <span
            className={`h-3 w-3 rounded-full bg-green-400 inline-block`}
          ></span>
          <span className="ml-2">Custom domain connected</span>
        </>
      );
    } else {
      return (
        <>
          <span
            className={`h-3 w-3 rounded-full bg-slate-400 inline-block`}
          ></span>
          <span className="ml-2">No custom domain connected</span>
        </>
      );
    }
  };
  const { selectedMerchant, selectedLocation } = useSelector(
    (state) => state.app
  );
  const [submittingCustomDomain, setSubmittingCustomDomain] = useState(false);
  const [submittedCustomDomain, setSubmittedCustomDomain] = useState(false);
  const [customDomain, setCustomDomain] = useState("");
  const [currentJob, setCurrentJob] = useState(null);
  const [fullLocationDetail, setFullLocationDetail] = useState(null);
  const dispatch = useDispatch();
  const [customDomainCollapsed, setCustomDomainCollapsed] = useState(false);
  const [sitemapCollapsed, setSitemapCollapsed] = useState(false);

  useEffect(() => {
    getStore().then((res) => {
      setFullLocationDetail(res.store);
    });
  }, [selectedLocation]);

  useEffect(() => {
    getSellerCustomDomainJob()
      .then((res) => {
        if (res.job) {
          setCurrentJob(res.job);
          setCustomDomain(res.job.domain);
        } else {
          if (fullLocationDetail?.custom_direct_web_urls?.length) {
            setCustomDomain(fullLocationDetail.custom_direct_web_urls[0].domain);
          }
        }
      })
      .catch(() => {
        if (fullLocationDetail?.custom_direct_web_urls?.length) {
          setCustomDomain(fullLocationDetail.custom_direct_web_urls[0].domain);
        }
      });
  }, [fullLocationDetail]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        {/* Title */}
        <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
          Customization 
        </h1>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px">
          <div className="grow">
            {/* Panel body */}
            <div className="p-6 space-y-6">
              <div>
                <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-4">
                  Custom Domain
                  <div
                    className={
                      "transition-all duration-300 inline-block ml-2 cursor-pointer " +
                      (customDomainCollapsed ? "" : "scale-y-[-1]")
                    }
                    onClick={() =>
                      setCustomDomainCollapsed(!customDomainCollapsed)
                    }
                  >
                    <svg
                      className="w-3 h-3 shrink-0 ml-1 fill-current text-slate-400"
                      viewBox="0 0 12 12"
                    >
                      <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                    </svg>
                  </div>
                </h2>
                {/*animate*/}
                <div
                  className={`overflow-hidden transition-all duration-700 ${
                    customDomainCollapsed ? "h-0" : "h-auto"
                  }`}
                >
                  <div className="text-sm">
                    <p className="mb-4">
                      Connect your domain or subdomain to Saucey Direct and give
                      your store a fully branded web presence. Instead of using
                      a Saucey Direct URL (e.g.,
                      direct.saucey.com/shop/mystore), you can use your own
                      custom domain (e.g., shopmystore.com), creating a seamless
                      and professional shopping experience for your customers.
                    </p>
                    <p>
                      For more information on how to connect your domain,{" "}
                      <a
                        href="https://intercom.help/sauceydirect/en/articles/10860223-using-a-custom-domain-for-your-saucey-direct-store"
                        target="_blank"
                        className="text-indigo-500 underline cursor-pointer"
                      >
                        click here
                      </a>
                      .
                    </p>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium mb-1">
                      Custom domain / subdomain
                    </label>
                    <div className="flex gap-2 w-96">
                      <input
                        type="text"
                        className="form-input flex-grow"
                        placeholder="shopmystore.com"
                        value={customDomain}
                        onChange={(e) => setCustomDomain(e.target.value)}
                      />
                      <PrimaryButton
                        loading={submittingCustomDomain}
                        success={submittedCustomDomain}
                        onClick={async () => {
                          const replacing =
                            Boolean(
                              fullLocationDetail?.custom_direct_web_urls?.length
                            ) || !currentJob?.closed;
                          setSubmittingCustomDomain(true);
                          try {
                            const res = await submitCustomDomain({
                              type: "store",
                              domain: customDomain,
                            });
                            dispatch(
                              addToast({
                                type: "success",
                                timer: 5,
                                message: `Custom domain is being processed, check back later.`,
                              })
                            );
                            setSubmittedCustomDomain(true);
                            setCurrentJob(res.job);
                            setTimeout(() => {
                              setSubmittedCustomDomain(false);
                            }, 5000);
                          } catch (e) {
                            let message = "An error occurred";
                            try {
                              const serverMessage = e.response?.data?.message;
                              if (serverMessage) {
                                message = serverMessage;
                              }
                            } catch {}
                            message += ", domain is not submitted.";
                            dispatch(
                              addToast({
                                type: "error",
                                message: message,
                              })
                            );
                          } finally {
                            setSubmittingCustomDomain(false);
                          }
                        }}
                      >
                        {Boolean(
                          fullLocationDetail?.custom_direct_web_urls?.length
                        ) || !currentJob?.closed
                          ? "Replace"
                          : "Save"}
                      </PrimaryButton>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        <CustomDomainStatusDot />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px">
          <div className="p-6 space-y-6">
            <div>
              <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-4">
                Sitemap
                <div
                  className={
                    "transition-all duration-300 inline-block ml-2 cursor-pointer " +
                    (sitemapCollapsed ? "" : "scale-y-[-1]")
                  }
                  onClick={() => setSitemapCollapsed(!sitemapCollapsed)}
                >
                  <svg
                    className="w-3 h-3 shrink-0 ml-1 fill-current text-slate-400"
                    viewBox="0 0 12 12"
                  >
                    <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                  </svg>
                </div>
              </h2>
              {/*animate*/}
              <div
                className={`overflow-hidden transition-all duration-700 ${
                  sitemapCollapsed ? "h-0" : "h-auto"
                }`}
              >
                <div className="text-sm">
                  <p className="mb-4">
                    When you connect a custom domain, we automatically generate
                    a sitemap and submit it to major search engines like Google,
                    Bing, and Yahoo. This helps search engines find and index
                    your site more quickly, improving its visibility in search
                    results. By publishing a sitemap to your Saucey Direct
                    store, we ensure your website stays discoverable, making it
                    easier for potential visitors to find you online.
                  </p>
                  <p>
                    We continuously monitor your website for updates, scanning
                    for changes in products, pages, and other content once a
                    day. Whenever we detect an update, we automatically refresh
                    your sitemap and notify search engines to ensure your latest
                    content is indexed as quickly as possible.
                  </p>
                </div>
                {/*  list some search engines, and have green dot next to them*/}
                {["Google", "Bing", "Yahoo", "Yandex"].map((engine) => (
                  <div key={engine} className="flex items-center mt-4 text-sm">
                    <div
                      className={`h-3 w-3 rounded-full inline-block ${
                        fullLocationDetail?.custom_direct_web_urls?.length
                          ? "bg-green-400"
                          : "bg-red-500"
                      }`}
                    ></div>
                    <span className="ml-2">
                      {engine}{" "}
                      {!fullLocationDetail?.custom_direct_web_urls?.length &&
                        "- Requires active custom domain"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px">
          <Customize />
        </div>
      </div>

      <PremiumPaywall requiresAddon={['seo-boost']} requiresPlan={['growth']} />
    </div>
  );
}

export default Customization;
